import * as React from 'react';
import { z } from 'zod';
import { saveBlob } from '@/lib/utils/download';
import { prepareApiRequest, readApiResponse } from './client';

/**
 * Files the API serves behind sign-in: the engagement banner, rendered PDF
 * reports and engagement archives.
 *
 * The storage account allows no public blob access, so the API never hands
 * the browser a blob address. It returns an `/api/` path instead, and that
 * path needs the bearer token. A plain `<img src>`, `<object data>` or
 * `<a href>` sends no Authorization header, so the API would refuse it.
 * These helpers fetch the bytes with the session's credentials instead.
 */

/**
 * Fetch an API file with the session's credentials. A refusal throws the
 * same `ApiError` (and runs the same 401 recovery) as `apiClient`.
 */
export async function fetchAuthedFile(
  path: string,
  options: { accept?: string; signal?: AbortSignal } = {},
): Promise<Blob> {
  const { url, headers, correlationId } = await prepareApiRequest(path, {
    headers: { Accept: options.accept ?? '*/*' },
  });
  const res = await fetch(url, { headers, signal: options.signal });
  if (!res.ok) {
    // Throws for every non-2xx answer.
    await readApiResponse(res, z.never(), path, correlationId);
  }
  return res.blob();
}

/** Fetch an API file with the session's credentials and save it as `fileName`. */
export async function downloadAuthedFile(path: string, fileName: string): Promise<void> {
  saveBlob(fileName, await fetchAuthedFile(path));
}

export interface AuthedFileUrl {
  /** An address the page can show; null while the bytes load, after a failure, or with no path. */
  url: string | null;
  /** The API refused the file or the fetch failed. */
  failed: boolean;
}

/**
 * The address an `<img>` or `<object>` can show for a file the API serves
 * behind sign-in.
 *
 * For an `/api/` path this fetches the bytes and returns an object URL for
 * them, revoked when the path changes or the component unmounts. Any other
 * address (an absolute URL, a data: URL) passes through unchanged.
 */
export function useAuthedFileUrl(src: string | null | undefined, accept = '*/*'): AuthedFileUrl {
  const isApiPath = typeof src === 'string' && src.startsWith('/api/');
  // Keyed by the path it was loaded for, so a changed path never shows the
  // previous (already revoked) object URL.
  const [loaded, setLoaded] = React.useState<{ src: string; url: string } | null>(null);
  const [failedSrc, setFailedSrc] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!isApiPath || !src) return;
    const controller = new AbortController();
    let objectUrl: string | null = null;
    void (async () => {
      try {
        const blob = await fetchAuthedFile(src, { accept, signal: controller.signal });
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setLoaded({ src, url: objectUrl });
      } catch {
        if (!controller.signal.aborted) setFailedSrc(src);
      }
    })();
    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src, isApiPath, accept]);

  if (!src) return { url: null, failed: false };
  if (!isApiPath) return { url: src, failed: false };
  if (loaded?.src === src) return { url: loaded.url, failed: false };
  return { url: null, failed: failedSrc === src };
}
